import React, { Fragment, useState, useEffect } from "react";
import {
  Row,
  Col,
  Card,
  CardBody,
  CardHeader,
  Button,
  Spinner,
  Badge,
} from "reactstrap";
import {
  Tag,
  Users,
  Filter,
  CheckSquare,
  Square,
  ChevronRight,
  AlertCircle,
  CheckCircle,
} from "react-feather";
import DataTable from "react-data-table-component";
import axios from "axios";
import { toast } from "react-toastify";
import { baseURL } from "../../../../../Services/api/baseURL";
import useRbac from "../../../../../Hooks/useRbac";

/* ─── helpers ───────────────────────────────────────────────── */

const getToken = () => JSON.parse(localStorage.getItem("token"));
const authHeaders = () => ({ Authorization: getToken() });

/* ─── small sub-components ───────────────────────────────────── */

const StepBadge = ({ step, active, done }) => (
  <div
    style={{
      width: 32,
      height: 32,
      borderRadius: "50%",
      display: "flex",
      alignItems: "center",
      justifyContent: "center",
      fontWeight: 700,
      fontSize: 14,
      flexShrink: 0,
      backgroundColor: done ? "#10b981" : active ? "#d3178a" : "#e5e7eb",
      color: done || active ? "#fff" : "#6b7280",
      transition: "all 0.3s",
    }}
  >
    {done ? <CheckCircle size={16} /> : step}
  </div>
);

const StepHeader = ({ step, title, active, done }) => (
  <div className="d-flex align-items-center gap-2 mb-3">
    <StepBadge step={step} active={active} done={done} />
    <h6
      style={{
        margin: 0,
        fontWeight: 600,
        color: done ? "#10b981" : active ? "#d3178a" : "#9ca3af",
        fontSize: 15,
        transition: "color 0.3s",
      }}
    >
      {title}
    </h6>
  </div>
);

/* ─── main component ─────────────────────────────────────────── */

const ApplyTags = () => {
  const { can } = useRbac();

  /* ---------- data ---------- */
  const [categories, setCategories] = useState([]);
  const [selectedCategory, setSelectedCategory] = useState(null);
  const [filteredCustomers, setFilteredCustomers] = useState([]);
  const [allCustomers, setAllCustomers] = useState([]);
  const [selectedCustomerIds, setSelectedCustomerIds] = useState([]);
  const [tags, setTags] = useState([]);
  const [selectedTagIds, setSelectedTagIds] = useState([]);
  const [customerSearch, setCustomerSearch] = useState("");

  /* ---------- loading / ui ---------- */
  const [loadingCategories, setLoadingCategories] = useState(false);
  const [loadingCustomers, setLoadingCustomers] = useState(false);
  const [loadingTags, setLoadingTags] = useState(false);
  const [applying, setApplying] = useState(false);
  const [applied, setApplied] = useState(false);

  /* ---------- derived state ---------- */
  const step = selectedCategory
    ? selectedTagIds.length > 0
      ? 3
      : 2
    : 1;

  const displayedCustomers = customerSearch.trim()
    ? filteredCustomers.filter((c) => {
        const q = customerSearch.toLowerCase();
        return (
          (c.first_name + " " + c.last_name).toLowerCase().includes(q) ||
          (c.email || "").toLowerCase().includes(q) ||
          (c.phoneNumber || "").toLowerCase().includes(q)
        );
      })
    : filteredCustomers;

  const allSelected =
    displayedCustomers.length > 0 &&
    displayedCustomers.every((c) => selectedCustomerIds.includes(String(c._id)));

  /* ─── fetch categories on mount ───────────────────────────── */
  useEffect(() => {
    fetchCategories();
    fetchTags();
  }, []);

  const fetchCategories = async () => {
    setLoadingCategories(true);
    try {
      const res = await axios.get(`${baseURL}/api/apply-tags/categories`, {
        headers: authHeaders(),
      });
      setCategories(res.data.data || []);
    } catch (err) {
      console.error("fetchCategories error:", err.response?.status, err.response?.data || err.message);
      toast.error(
        `Failed to load product categories: ${err.response?.data?.message || err.message}`
      );
    } finally {
      setLoadingCategories(false);
    }
  };

  const fetchTags = async () => {
    setLoadingTags(true);
    try {
      const res = await axios.get(`${baseURL}/api/tags`, {
        headers: authHeaders(),
      });
      setTags(res.data.data || []);
    } catch (err) {
      console.error("fetchTags error:", err.response?.status, err.response?.data || err.message);
      toast.error("Failed to load tags");
    } finally {
      setLoadingTags(false);
    }
  };

  /* ─── fetch customers when category changes ───────────────── */
  const handleCategorySelect = async (cat) => {
    setSelectedCategory(cat);
    setSelectedCustomerIds([]);
    setSelectedTagIds([]);
    setApplied(false);
    setCustomerSearch("");
    setFilteredCustomers([]);
    setLoadingCustomers(true);
    try {
      const res = await axios.get(`${baseURL}/api/apply-tags/customers-by-category`, {
        params: { collectionId: cat._id },
        headers: authHeaders(),
      });
      const list = res.data.data?.customers || [];
      setFilteredCustomers(list);
      setAllCustomers(list);
      // Default: select all
      setSelectedCustomerIds(list.map((c) => String(c._id)));
    } catch (err) {
      console.error("getCustomersByCategory error:", err.response?.status, err.response?.data || err.message);
      toast.error("Failed to load customers for this category");
    } finally {
      setLoadingCustomers(false);
    }
  };

  /* ─── customer selection helpers ──────────────────────────── */
  const toggleSelectAll = () => {
    if (allSelected) {
      setSelectedCustomerIds((prev) =>
        prev.filter((id) => !displayedCustomers.map((c) => String(c._id)).includes(id))
      );
    } else {
      const toAdd = displayedCustomers.map((c) => String(c._id));
      setSelectedCustomerIds((prev) => [...new Set([...prev, ...toAdd])]);
    }
  };

  const toggleCustomer = (id) => {
    setSelectedCustomerIds((prev) =>
      prev.includes(String(id))
        ? prev.filter((x) => x !== String(id))
        : [...prev, String(id)]
    );
  };

  /* ─── tag selection helpers ────────────────────────────────── */
  const toggleTag = (tagId) => {
    setSelectedTagIds((prev) =>
      prev.includes(tagId) ? prev.filter((x) => x !== tagId) : [...prev, tagId]
    );
    setApplied(false);
  };

  /* ─── apply ────────────────────────────────────────────────── */
  const handleApply = async () => {
    if (selectedCustomerIds.length === 0) {
      toast.warn("Select at least one customer");
      return;
    }
    if (selectedTagIds.length === 0) {
      toast.warn("Select at least one tag");
      return;
    }
    setApplying(true);
    try {
      await axios.post(
        `${baseURL}/api/apply-tags/bulk-apply`,
        { customerIds: selectedCustomerIds, tagIds: selectedTagIds },
        { headers: authHeaders() }
      );
      toast.success(
        `${selectedTagIds.length} tag(s) applied to ${selectedCustomerIds.length} customer(s) successfully`
      );
      setApplied(true);
    } catch (err) {
      console.error("bulkApplyTags error:", err.response?.status, err.response?.data || err.message);
      toast.error(err.response?.data?.message || "Failed to apply tags");
    } finally {
      setApplying(false);
    }
  };

  /* ─── reset ────────────────────────────────────────────────── */
  const handleReset = () => {
    setSelectedCategory(null);
    setFilteredCustomers([]);
    setAllCustomers([]);
    setSelectedCustomerIds([]);
    setSelectedTagIds([]);
    setCustomerSearch("");
    setApplied(false);
  };

  /* ─── customer table columns ───────────────────────────────── */
  const customerColumns = [
    {
      name: (
        <div
          style={{ cursor: "pointer", display: "flex", alignItems: "center", gap: 6 }}
          onClick={toggleSelectAll}
        >
          {allSelected ? (
            <CheckSquare size={16} color="#d3178a" />
          ) : (
            <Square size={16} color="#6b7280" />
          )}
          <span style={{ fontWeight: 600, fontSize: 13 }}>
            {selectedCustomerIds.length > 0
              ? `${selectedCustomerIds.length} selected`
              : "Select All"}
          </span>
        </div>
      ),
      width: "160px",
      cell: (row) => (
        <div
          style={{ cursor: "pointer" }}
          onClick={() => toggleCustomer(row._id)}
        >
          {selectedCustomerIds.includes(String(row._id)) ? (
            <CheckSquare size={18} color="#d3178a" />
          ) : (
            <Square size={18} color="#9ca3af" />
          )}
        </div>
      ),
    },
    {
      name: "Customer",
      selector: (row) => `${row.first_name || ""} ${row.last_name || ""}`,
      cell: (row) => (
        <div>
          <div style={{ fontWeight: 600, fontSize: 14 }}>
            {`${row.first_name || ""} ${row.last_name || ""}`.trim() || "—"}
          </div>
          <div style={{ fontSize: 12, color: "#6b7280" }}>{row.email}</div>
        </div>
      ),
      sortable: true,
      grow: 2,
    },
    {
      name: "Phone",
      selector: (row) => row.phoneNumber,
      cell: (row) => (
        <span style={{ fontSize: 13, color: "#374151" }}>
          {row.phoneNumber || "—"}
        </span>
      ),
    },
    {
      name: "Current Tags",
      cell: (row) => (
        <div style={{ display: "flex", flexWrap: "wrap", gap: 4 }}>
          {row.tags && row.tags.length > 0 ? (
            <span
              style={{
                fontSize: 11,
                padding: "2px 8px",
                borderRadius: 12,
                background: "#f3f4f6",
                color: "#374151",
              }}
            >
              {row.tags.length} tag{row.tags.length !== 1 ? "s" : ""}
            </span>
          ) : (
            <span style={{ fontSize: 11, color: "#9ca3af" }}>No tags</span>
          )}
        </div>
      ),
    },
  ];

  /* ─── conditional row styles ────────────────────────────────── */
  const conditionalRowStyles = [
    {
      when: (row) => selectedCustomerIds.includes(String(row._id)),
      style: {
        backgroundColor: "#fdf2f8",
        borderLeft: "3px solid #d3178a",
      },
    },
  ];

  /* ─── render ───────────────────────────────────────────────── */
  return (
    <Fragment>
      {/* ── Page Header ── */}
      <Row className="mb-3 align-items-center">
        <Col md={8}>
          <div className="d-flex align-items-center gap-2">
            <Tag size={22} color="#d3178a" />
            <h5 style={{ margin: 0, fontWeight: 700, color: "#1f2937" }}>
              Apply Tags
            </h5>
          </div>
          <p style={{ margin: 0, fontSize: 13, color: "#6b7280", marginTop: 4 }}>
            Filter customers by product category and apply tags in bulk.
          </p>
        </Col>
        {selectedCategory && (
          <Col md={4} className="text-end">
            <Button
              size="sm"
              outline
              color="secondary"
              onClick={handleReset}
            >
              Reset & Start Over
            </Button>
          </Col>
        )}
      </Row>

      {/* ─────────────────────────────────────────────────────────── */}
      {/* STEP 1 — Product Category Filter                           */}
      {/* ─────────────────────────────────────────────────────────── */}
      <Card className="mb-3" style={{ border: "1px solid #f0f0f0" }}>
        <CardHeader
          style={{
            background: step >= 1 ? "#fff0f8" : "#fafafa",
            borderBottom: "1px solid #f0f0f0",
            padding: "14px 20px",
          }}
        >
          <StepHeader
            step={1}
            title="Select Product Category"
            active={step === 1}
            done={!!selectedCategory}
          />
          {selectedCategory && (
            <div style={{ marginLeft: 40, marginTop: -4 }}>
              <Badge
                style={{
                  background: "#d3178a",
                  color: "#fff",
                  fontWeight: 500,
                  fontSize: 12,
                  padding: "4px 12px",
                  borderRadius: 12,
                }}
              >
                {selectedCategory.collection_name}
              </Badge>
              <span style={{ fontSize: 12, color: "#6b7280", marginLeft: 8 }}>
                (click another to change)
              </span>
            </div>
          )}
        </CardHeader>
        <CardBody style={{ padding: "16px 20px" }}>
          {loadingCategories ? (
            <div className="d-flex align-items-center gap-2">
              <Spinner size="sm" color="primary" />
              <span style={{ fontSize: 13, color: "#6b7280" }}>
                Loading categories…
              </span>
            </div>
          ) : categories.length === 0 ? (
            <div
              className="d-flex align-items-center gap-2"
              style={{ color: "#9ca3af", fontSize: 13 }}
            >
              <AlertCircle size={16} />
              No active product categories found.
            </div>
          ) : (
            <div style={{ display: "flex", flexWrap: "wrap", gap: 10 }}>
              {categories.map((cat) => {
                const isActive =
                  selectedCategory && selectedCategory._id === cat._id;
                return (
                  <button
                    key={cat._id}
                    onClick={() => handleCategorySelect(cat)}
                    style={{
                      padding: "8px 18px",
                      borderRadius: 20,
                      border: `2px solid ${isActive ? "#d3178a" : "#e5e7eb"}`,
                      background: isActive ? "#d3178a" : "#fff",
                      color: isActive ? "#fff" : "#374151",
                      fontWeight: isActive ? 600 : 400,
                      fontSize: 13,
                      cursor: "pointer",
                      transition: "all 0.2s",
                      display: "flex",
                      alignItems: "center",
                      gap: 6,
                    }}
                  >
                    <Filter size={13} />
                    {cat.collection_name}
                    {isActive && <ChevronRight size={13} />}
                  </button>
                );
              })}
            </div>
          )}
        </CardBody>
      </Card>

      {/* ─────────────────────────────────────────────────────────── */}
      {/* STEP 2 — Customer List                                      */}
      {/* ─────────────────────────────────────────────────────────── */}
      {selectedCategory && (
        <Card className="mb-3" style={{ border: "1px solid #f0f0f0" }}>
          <CardHeader
            style={{
              background: "#fafafa",
              borderBottom: "1px solid #f0f0f0",
              padding: "14px 20px",
            }}
          >
            <div className="d-flex align-items-center justify-content-between">
              <StepHeader
                step={2}
                title="Select Customers"
                active={true}
                done={selectedCustomerIds.length > 0}
              />
              <div className="d-flex align-items-center gap-2">
                {/* search */}
                <div
                  className="form-group position-relative search_outer d-flex align-items-center mb-0"
                  style={{ minWidth: 220 }}
                >
                  <i className="fa fa-search" style={{ color: "#9ca3af" }} />
                  <input
                    className="form-control border-0 ms-2"
                    type="text"
                    placeholder="Search by name / email…"
                    value={customerSearch}
                    onChange={(e) => setCustomerSearch(e.target.value)}
                    style={{ fontSize: 13 }}
                  />
                </div>
                <div
                  style={{
                    fontSize: 13,
                    color: "#6b7280",
                    whiteSpace: "nowrap",
                  }}
                >
                  <Users size={14} className="me-1" />
                  {filteredCustomers.length} customer
                  {filteredCustomers.length !== 1 ? "s" : ""} found
                </div>
              </div>
            </div>
          </CardHeader>
          <CardBody style={{ padding: 0 }}>
            {loadingCustomers ? (
              <div
                className="d-flex justify-content-center align-items-center"
                style={{ padding: 40 }}
              >
                <Spinner color="primary" />
                <span style={{ marginLeft: 10, color: "#6b7280" }}>
                  Loading customers…
                </span>
              </div>
            ) : filteredCustomers.length === 0 ? (
              <div
                className="d-flex flex-column align-items-center justify-content-center"
                style={{ padding: 40, color: "#9ca3af" }}
              >
                <Users size={36} style={{ marginBottom: 12, opacity: 0.4 }} />
                <div style={{ fontWeight: 500, fontSize: 14 }}>
                  No customers found for this category
                </div>
                <div style={{ fontSize: 13, marginTop: 4 }}>
                  No delivered orders found containing products from{" "}
                  <strong>{selectedCategory.collection_name}</strong>.
                </div>
              </div>
            ) : (
              <DataTable
                columns={customerColumns}
                data={displayedCustomers}
                pagination
                paginationPerPage={10}
                paginationRowsPerPageOptions={[10, 25, 50]}
                highlightOnHover
                pointerOnHover
                onRowClicked={(row) => toggleCustomer(row._id)}
                conditionalRowStyles={conditionalRowStyles}
                noHeader
                dense
                customStyles={{
                  rows: {
                    style: {
                      minHeight: 52,
                      cursor: "pointer",
                    },
                  },
                  headCells: {
                    style: {
                      background: "#f9fafb",
                      fontWeight: 600,
                      fontSize: 12,
                      color: "#374151",
                      paddingTop: 10,
                      paddingBottom: 10,
                    },
                  },
                }}
              />
            )}
          </CardBody>
          {selectedCustomerIds.length > 0 && (
            <div
              style={{
                padding: "10px 20px",
                background: "#fdf2f8",
                borderTop: "1px solid #f9d5eb",
                fontSize: 13,
                color: "#9d174d",
                fontWeight: 500,
              }}
            >
              ✓ {selectedCustomerIds.length} customer
              {selectedCustomerIds.length !== 1 ? "s" : ""} selected
            </div>
          )}
        </Card>
      )}

      {/* ─────────────────────────────────────────────────────────── */}
      {/* STEP 3 — Choose Tags                                        */}
      {/* ─────────────────────────────────────────────────────────── */}
      {selectedCategory && (
        <Card className="mb-3" style={{ border: "1px solid #f0f0f0" }}>
          <CardHeader
            style={{
              background: selectedTagIds.length > 0 ? "#fff0f8" : "#fafafa",
              borderBottom: "1px solid #f0f0f0",
              padding: "14px 20px",
            }}
          >
            <StepHeader
              step={3}
              title="Choose Tags to Apply"
              active={selectedCustomerIds.length > 0}
              done={selectedTagIds.length > 0}
            />
          </CardHeader>
          <CardBody style={{ padding: "16px 20px" }}>
            {loadingTags ? (
              <div className="d-flex align-items-center gap-2">
                <Spinner size="sm" color="primary" />
                <span style={{ fontSize: 13, color: "#6b7280" }}>
                  Loading tags…
                </span>
              </div>
            ) : tags.length === 0 ? (
              <div
                style={{ color: "#9ca3af", fontSize: 13 }}
                className="d-flex align-items-center gap-2"
              >
                <AlertCircle size={15} />
                No active tags found. Create tags first in Tag Management.
              </div>
            ) : (
              <>
                <p
                  style={{
                    fontSize: 12,
                    color: "#6b7280",
                    marginBottom: 12,
                  }}
                >
                  Click tags to select / deselect. Selected tags will be
                  appended to the chosen customers.
                </p>
                <div style={{ display: "flex", flexWrap: "wrap", gap: 8 }}>
                  {tags.map((tag) => {
                    const isSelected = selectedTagIds.includes(tag._id);
                    return (
                      <button
                        key={tag._id}
                        onClick={() => toggleTag(tag._id)}
                        style={{
                          display: "flex",
                          alignItems: "center",
                          gap: 6,
                          padding: "6px 14px",
                          borderRadius: 16,
                          border: `2px solid ${
                            isSelected ? tag.color || "#d3178a" : "#e5e7eb"
                          }`,
                          background: isSelected
                            ? tag.color || "#d3178a"
                            : "#fff",
                          color: isSelected ? "#fff" : "#374151",
                          fontWeight: isSelected ? 600 : 400,
                          fontSize: 13,
                          cursor: "pointer",
                          transition: "all 0.2s",
                        }}
                      >
                        <span
                          style={{
                            width: 8,
                            height: 8,
                            borderRadius: "50%",
                            background: isSelected
                              ? "rgba(255,255,255,0.7)"
                              : tag.color || "#9ca3af",
                            flexShrink: 0,
                          }}
                        />
                        {tag.display_name}
                        {isSelected && (
                          <CheckCircle size={13} />
                        )}
                      </button>
                    );
                  })}
                </div>
              </>
            )}
          </CardBody>
        </Card>
      )}

      {/* ─────────────────────────────────────────────────────────── */}
      {/* STEP 4 — Summary + Apply Button                             */}
      {/* ─────────────────────────────────────────────────────────── */}
      {selectedCategory && selectedCustomerIds.length > 0 && selectedTagIds.length > 0 && (
        <Card
          style={{
            border: applied ? "2px solid #10b981" : "2px solid #d3178a",
            transition: "border-color 0.3s",
          }}
        >
          <CardBody style={{ padding: "20px 24px" }}>
            <Row className="align-items-center">
              <Col md={8}>
                <div className="d-flex align-items-center gap-2 mb-3">
                  {applied ? (
                    <CheckCircle size={20} color="#10b981" />
                  ) : (
                    <Tag size={20} color="#d3178a" />
                  )}
                  <h6
                    style={{
                      margin: 0,
                      fontWeight: 700,
                      color: applied ? "#10b981" : "#d3178a",
                    }}
                  >
                    {applied ? "Tags Applied Successfully!" : "Summary"}
                  </h6>
                </div>

                {/* Tags summary */}
                <div style={{ marginBottom: 10 }}>
                  <span
                    style={{
                      fontSize: 12,
                      color: "#6b7280",
                      fontWeight: 600,
                      textTransform: "uppercase",
                      letterSpacing: 0.5,
                    }}
                  >
                    Tags to apply ({selectedTagIds.length})
                  </span>
                  <div
                    style={{
                      display: "flex",
                      flexWrap: "wrap",
                      gap: 6,
                      marginTop: 6,
                    }}
                  >
                    {tags
                      .filter((t) => selectedTagIds.includes(t._id))
                      .map((t) => (
                        <span
                          key={t._id}
                          style={{
                            padding: "3px 10px",
                            borderRadius: 12,
                            background: t.color || "#d3178a",
                            color: "#fff",
                            fontSize: 12,
                            fontWeight: 500,
                          }}
                        >
                          {t.display_name}
                        </span>
                      ))}
                  </div>
                </div>

                {/* Customer count */}
                <div
                  style={{
                    fontSize: 14,
                    color: "#374151",
                    display: "flex",
                    alignItems: "center",
                    gap: 6,
                  }}
                >
                  <Users size={15} color="#d3178a" />
                  <strong>{selectedCustomerIds.length}</strong> customer
                  {selectedCustomerIds.length !== 1 ? "s" : ""} will be tagged
                  {selectedCategory && (
                    <span style={{ color: "#9ca3af", fontSize: 13 }}>
                      {" "}
                      (filtered from{" "}
                      <em>{selectedCategory.collection_name}</em> category)
                    </span>
                  )}
                </div>

                {!applied && (
                  <p
                    style={{
                      fontSize: 12,
                      color: "#9ca3af",
                      marginTop: 8,
                      marginBottom: 0,
                    }}
                  >
                    Existing tags on customers will be kept — only the selected
                    tags will be added.
                  </p>
                )}
              </Col>

              <Col
                md={4}
                className="d-flex justify-content-end align-items-center"
              >
                {applied ? (
                  <Button
                    color="success"
                    outline
                    onClick={handleReset}
                    style={{ minWidth: 140 }}
                  >
                    Start New Session
                  </Button>
                ) : can("customers.update") ? (
                  <Button
                    onClick={handleApply}
                    disabled={applying}
                    style={{
                      background: "#d3178a",
                      border: "none",
                      minWidth: 160,
                      fontWeight: 600,
                      fontSize: 14,
                      padding: "10px 24px",
                      borderRadius: 8,
                    }}
                  >
                    {applying ? (
                      <>
                        <Spinner size="sm" className="me-2" />
                        Applying…
                      </>
                    ) : (
                      <>
                        <Tag size={15} className="me-2" />
                        Apply Tags
                      </>
                    )}
                  </Button>
                ) : (
                  <span style={{ fontSize: 13, color: "#9ca3af" }}>
                    No permission to apply tags
                  </span>
                )}
              </Col>
            </Row>
          </CardBody>
        </Card>
      )}
    </Fragment>
  );
};

export default ApplyTags;
